import { normalizeContentToHtml, sanitizeWikiHtml } from './wiki.richText';
import { companyDocumentIdentity, openHtmlDocument } from '../../../shared/documents';

// Le Wiki conserve sa composition éditoriale propre, mais s'appuie désormais
// sur l'identité documentaire et le moteur d'ouverture/impression communs.
const wikiPdfTemplate = {
  documentLabel: 'Espace documentaire',
  footer: `Document interne - ${companyDocumentIdentity.legalName}`,
};

const escapeHtml = (value = '') => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const formatDate = (value) => {
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.getTime())
    ? escapeHtml(value || '')
    : new Intl.DateTimeFormat('fr-FR').format(date);
};

export const buildWikiPdfHtml = ({ article, categoryLabel, logoUrl }) => {
  const resolvedLogo = logoUrl || companyDocumentIdentity.logoUrl || '';
  const logoSrc = resolvedLogo ? new URL(resolvedLogo, window.location.origin).href : '';
  const rawContent = article.renderedHtml || article.content || article.plainText || '';
  const content = sanitizeWikiHtml(normalizeContentToHtml(rawContent));

  return `<!doctype html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(article.title || 'Article Wiki')}</title>
  <style>
    @page { size: A4; margin: 12mm 18mm; }
    * { box-sizing: border-box; }
    body { color: #202124; font-family: Arial, Helvetica, sans-serif; font-size: 11pt; line-height: 1.55; margin: 0; }
    .print-layout { border-collapse: collapse; height: 273mm; table-layout: fixed; width: 100%; }
    .print-layout > thead { display: table-header-group; }
    .print-layout > tfoot { display: table-footer-group; }
    .print-layout > tbody { height: 100%; }
    .print-layout > tbody > tr > td, .print-layout > thead > tr > td, .print-layout > tfoot > tr > td { border: 0; padding: 0; }
    .print-layout__header-cell { padding-bottom: 8mm !important; }
    .print-layout__footer-cell { padding-top: 8mm !important; }
    .print-layout__content-cell { vertical-align: top; }
    .document-header { align-items: center; background: #fff; border-bottom: 2px solid #26364a; display: flex; height: 22mm; justify-content: space-between; padding-bottom: 4mm; width: 100%; }
    .document-header img { max-height: 15mm; max-width: 42mm; object-fit: contain; }
    .document-header__text { color: #52606f; text-align: right; }
    .document-header__text strong { color: #26364a; display: block; font-size: 11pt; }
    .document-footer { background: #fff; border-top: 1px solid #aeb7c2; color: #697586; display: grid; font-size: 7.5pt; gap: 1.5mm; line-height: 1.25; min-height: 22mm; padding-top: 2.5mm; width: 100%; }
    .document-footer p { margin: 0; }
    .document-footer__identity { color: #344054; font-size: 8pt; font-weight: 700; }
    .document-footer__legal { overflow-wrap: anywhere; }
    .document-footer__meta { align-items: center; display: flex; justify-content: space-between; }
    .article-heading { border-bottom: 1px solid #d7dde4; margin-bottom: 9mm; padding-bottom: 6mm; }
    h1 { color: #172b4d; font-size: 25pt; line-height: 1.18; margin: 0 0 4mm; }
    .metadata { color: #5c6877; display: flex; flex-wrap: wrap; font-size: 9.5pt; gap: 2mm 7mm; }
    .article-content h1, .article-content h2, .article-content h3 { break-after: avoid; color: #172b4d; margin: 8mm 0 3mm; }
    .article-content h1 { font-size: 20pt; }
    .article-content h2 { font-size: 16pt; }
    .article-content h3 { font-size: 13pt; }
    .article-content p { margin: 0 0 4mm; }
    .article-content blockquote { border-left: 3px solid #73849a; color: #52606f; margin: 5mm 0; padding: 2mm 0 2mm 5mm; }
    .article-content table { border-collapse: collapse; margin: 5mm 0; width: 100%; }
    .article-content th, .article-content td { border: 1px solid #cbd2da; padding: 2.5mm; text-align: left; vertical-align: top; }
    .article-content th { background: #eef1f5; }
    .article-content img { height: auto; max-width: 100%; }
    .article-content a { color: #245ea8; overflow-wrap: anywhere; }
    .article-content pre { background: #f3f5f7; overflow-wrap: anywhere; padding: 4mm; white-space: pre-wrap; }
    .article-content tr, .article-content img, .article-content blockquote, .article-content pre { break-inside: avoid; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .document-header, .document-footer { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <table class="print-layout">
    <thead>
      <tr>
        <td class="print-layout__header-cell">
          <header class="document-header">
            ${logoSrc ? `<img src="${escapeHtml(logoSrc)}" alt="${escapeHtml(companyDocumentIdentity.legalName)}" />` : `<strong>${escapeHtml(companyDocumentIdentity.legalName)}</strong>`}
            <div class="document-header__text"><strong>Wiki</strong>${escapeHtml(wikiPdfTemplate.documentLabel)}</div>
          </header>
        </td>
      </tr>
    </thead>
    <tfoot>
      <tr>
        <td class="print-layout__footer-cell">
          <footer class="document-footer">
            <p class="document-footer__identity">${escapeHtml(companyDocumentIdentity.displayName)}</p>
            <p class="document-footer__legal">${escapeHtml([...companyDocumentIdentity.addressLines, ...companyDocumentIdentity.contactLines, ...companyDocumentIdentity.legalLines].join(' – '))}</p>
            <div class="document-footer__meta">
              <span>${escapeHtml(wikiPdfTemplate.footer)}</span>
            </div>
          </footer>
        </td>
      </tr>
    </tfoot>
    <tbody>
      <tr>
        <td class="print-layout__content-cell">
          <main>
            <section class="article-heading">
              <h1>${escapeHtml(article.title || 'Article Wiki')}</h1>
              <div class="metadata">
                <span>${escapeHtml(categoryLabel || article.category || '')}</span>
                <span>${escapeHtml(article.type || '')}</span>
                <span>Version ${escapeHtml(article.version || '-')}</span>
                <span>Mise à jour le ${formatDate(article.updatedAt)}</span>
              </div>
            </section>
            <article class="article-content">${content}</article>
          </main>
        </td>
      </tr>
    </tbody>
  </table>
</body>
</html>`;
};

export const exportWikiArticlePdf = async ({ article, categoryLabel, logoUrl }) => {
  try {
    await openHtmlDocument({
      html: buildWikiPdfHtml({ article, categoryLabel, logoUrl }),
      print: true,
      windowFeatures: 'width=1024,height=768',
      waitForResources: true,
      resourcesFallbackDelay: 2000,
    });
    return true;
  } catch (error) {
    window.alert(error?.message || "Impossible d'ouvrir la fenêtre d'export. Vérifiez le blocage des pop-ups.");
    return false;
  }
};
